import { history } from '@umijs/max';
import { useEffect, useRef } from 'react';
import {
  appendTrackingParamsToHref,
  hasTrackingParams,
  mergeTrackingParams,
} from '@/common/utils-business/attribution-params.mjs';

function getCurrentInternalHref() {
  const { pathname, search, hash } = window.location;

  return `${pathname}${search}${hash}`;
}

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function getInternalNavigationHref(href: string) {
  const targetUrl = new URL(href, window.location.href);

  if (targetUrl.origin !== window.location.origin) {
    return null;
  }

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
}

function appendTrackingParamsToAnchor(anchor: HTMLAnchorElement, trackingParams: URLSearchParams) {
  const href = anchor.getAttribute('href');

  if (!href || anchor.hasAttribute('download')) {
    return href;
  }

  const hrefWithTrackingParams = appendTrackingParamsToHref(href, trackingParams, window.location);

  if (hrefWithTrackingParams !== href) {
    anchor.setAttribute('href', hrefWithTrackingParams);
  }

  return hrefWithTrackingParams;
}

function appendTrackingParamsToCurrentLinks(
  trackingParams: URLSearchParams,
  root: ParentNode = document,
) {
  root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
    appendTrackingParamsToAnchor(anchor, trackingParams);
  });
}

export function AttributionParamPreserver() {
  const trackingParamsRef = useRef(new URLSearchParams());

  useEffect(() => {
    function captureCurrentTrackingParams() {
      trackingParamsRef.current = mergeTrackingParams(
        trackingParamsRef.current,
        window.location.search,
      );
    }

    function syncCurrentUrl() {
      captureCurrentTrackingParams();

      if (!hasTrackingParams(trackingParamsRef.current)) {
        return;
      }

      const currentHref = getCurrentInternalHref();
      const hrefWithTrackingParams = appendTrackingParamsToHref(
        currentHref,
        trackingParamsRef.current,
        window.location,
      );

      if (hrefWithTrackingParams !== currentHref) {
        history.replace(hrefWithTrackingParams);
        return;
      }

      appendTrackingParamsToCurrentLinks(trackingParamsRef.current);
    }

    function handleHistoryNavigation() {
      syncCurrentUrl();
    }

    function handleDocumentClick(event: MouseEvent) {
      if (event.defaultPrevented || !(event.target instanceof Element)) {
        return;
      }

      const anchor = event.target.closest<HTMLAnchorElement>('a[href]');
      const href = anchor?.getAttribute('href');

      if (!anchor || !href || anchor.hasAttribute('download')) {
        return;
      }

      captureCurrentTrackingParams();

      if (!hasTrackingParams(trackingParamsRef.current)) {
        return;
      }

      const hrefWithTrackingParams = appendTrackingParamsToAnchor(
        anchor,
        trackingParamsRef.current,
      );

      if (!hrefWithTrackingParams) {
        return;
      }

      const internalNavigationHref = getInternalNavigationHref(hrefWithTrackingParams);

      if (!internalNavigationHref || (anchor.target && anchor.target !== '_self')) {
        return;
      }

      if (isModifiedClick(event)) {
        return;
      }

      if (hrefWithTrackingParams !== href || hasTrackingParams(window.location.search)) {
        event.preventDefault();
        history.push(internalNavigationHref);
      }
    }

    const observer = new MutationObserver((mutations) => {
      if (!hasTrackingParams(trackingParamsRef.current)) {
        return;
      }

      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
          appendTrackingParamsToAnchor(mutation.target, trackingParamsRef.current);
          continue;
        }

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node instanceof HTMLAnchorElement && node.hasAttribute('href')) {
            appendTrackingParamsToAnchor(node, trackingParamsRef.current);
          }

          appendTrackingParamsToCurrentLinks(trackingParamsRef.current, node);
        });
      }
    });

    syncCurrentUrl();
    observer.observe(document.body, {
      attributeFilter: ['href'],
      attributes: true,
      childList: true,
      subtree: true,
    });
    document.addEventListener('click', handleDocumentClick, { capture: true });
    window.addEventListener('pageshow', handleHistoryNavigation);
    window.addEventListener('popstate', handleHistoryNavigation);
    const unlisten = history.listen(handleHistoryNavigation);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleDocumentClick, { capture: true });
      window.removeEventListener('pageshow', handleHistoryNavigation);
      window.removeEventListener('popstate', handleHistoryNavigation);
      unlisten();
    };
  }, []);

  return null;
}
